import { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Pagination,
  Stack,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import { useDialog, useRequestState } from "@app/hooks";
import { Strings } from "@app/constants";
import {
  selectCustomerState,
  selectDeleteAssignCustomersState,
  selectSalesPersonDetailsState,
} from "../selector";
import { DeleteCustomerPopup } from "./delete-customer-popup";
import { customer, salesPersonDetails } from "../slice";

const SalesPersonDetails = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const deleteDialog = useDialog();

  const salesPersonId = location.state?.salesPerson;

  const [page, setPage] = useState(1);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null
  );

  const rowsPerPage = 10;

  const handleDeleteClick = (customerId: number) => {
    setSelectedCustomerId(customerId);
    deleteDialog.show();
  };

  const { data } = useRequestState({
    stateSelector: selectCustomerState,
    successMessageShown: true,
  });
  const onSuccess = useCallback(() => {
    deleteDialog.hide();
    setSelectedCustomerId(null);
    dispatch(customer(salesPersonId?.role))
  }, []);
  
  const filteredData = data?.filter((d:any)=> d?.id === salesPersonId?.id)
  
  useRequestState({
    stateSelector: selectDeleteAssignCustomersState,
    errorShown: true,
    successMessageShown: true,
    onSuccess: onSuccess,
  });

  useEffect(()=>{
    dispatch(customer(salesPersonId?.role))
  },[])
  
  // useEffect(() => {
  //   if (salesPersonId?.id) {
  //     dispatch(salesPersonDetails({ salesPersonId: salesPersonId?.id }));
  //   }
  // }, [salesPersonId, dispatch]);

  const paginatedData = Array?.isArray(filteredData?.[0]?.assignedCustomers)
    ? filteredData?.[0]?.assignedCustomers?.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
      )
     :[] ;
      
  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <Stack sx={{ p: { xs: 2, sm: 4 } }}>
      <Card
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "center",
          textAlign: { xs: "center", sm: "left" },
          mb: 4,
          p: 2,
        }}
      >
        <Avatar
          src={salesPersonId?.profileImage}
          sx={{
            width: 100,
            height: 100,
            mr: { sm: 4 },
            mb: { xs: 2, sm: 0 },
          }}
        >
          {!salesPersonId?.profileImage &&
            salesPersonId?.firstName &&
            `${salesPersonId?.firstName[0] || ""}${
              salesPersonId?.lastName?.[0] || ""
            }`.toUpperCase()}
        </Avatar>

        <CardContent sx={{ width: "100%" }}>
          {[
            {
              label: "Name",
              value: salesPersonId
                ? `${salesPersonId?.firstName} ${salesPersonId?.lastName}`
                : "N/A",
            },
            {
              label: "Contact",
              value: salesPersonId?.phone || "N/A",
            },
            {
              label: "Email",
              value: salesPersonId?.email || "N/A",
            },
            {
              label: "Assigned Customer",
              value: filteredData?.[0]?.assignedCustomers?.length || 0,
            },
          ]?.map((item, idx) => (
            <Stack
              key={idx}
              sx={{
                mb: 1,
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                alignItems: {
                  xs: "center",
                  sm: "baseline",
                },
                justifyContent: {
                  xs: "center",
                  sm: "flex-start",
                },
                "@media (max-width:800px)": {
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                },
              }}
            >
              <Typography
                sx={{
                  color: "#404040",
                  fontWeight: 600,
                  width: { sm: "30%", xs: "100%" },
                  mb: { xs: 0.5, sm: 0 },
                  maxWidth: "20%",
                }}
              >
                {item.label}:
              </Typography>
              <Typography
                sx={{
                  color: "#5F5F5F",
                  fontSize: 15,
                  wordBreak: "break-word",
                  alignSelf: "center",
                  justifyContent: "center",
                }}
              >
                {item.value}
              </Typography>
            </Stack>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography
            sx={{
              color: "#000000",
              fontFamily: fontFamily,
              fontWeight: "500",
            }}
          >
            Assigned Customer
          </Typography>
          <TableContainer
            component={Paper}
            sx={{
              mt: 2,
              overflow: "hidden",
              borderLeft: "1px solid #ccc",
              borderRight: "1px solid #ccc",
            }}
          >
            <Stack
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <Table size="medium" stickyHeader>
                <TableHead sx={{ backgroundColor: "#f4f6f8" }}>
                  <TableRow>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#474747",
                        fontFamily: fontFamily,
                        fontWeight: 600,
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#474747",
                        fontFamily: fontFamily,
                        fontWeight: 600,
                      }}
                    >
                      Contact
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#474747",
                        fontFamily: fontFamily,
                        fontWeight: 600,
                      }}
                    >
                      Email
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#474747",
                        fontFamily: fontFamily,
                        fontWeight: 600,
                      }}
                    >
                      Delete
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData?.length > 0 ? (
                    paginatedData?.map((customer: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell
                          align="center"
                          sx={{
                            color: "#5F5F5F",
                            fontFamily: fontFamily,
                            fontWeight: 500,
                          }}
                        >
                          {`${customer.firstName} ${customer.lastName}`}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color: "#5F5F5F",
                            fontFamily: fontFamily,
                            fontWeight: 500,
                          }}
                        >
                          {customer.phone}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color: "#5F5F5F",
                            fontFamily: fontFamily,
                            fontWeight: 500,
                          }}
                        >
                          {customer.email}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => handleDeleteClick(customer?.id)}
                            color="error"
                          >
                            <Icon
                              icon="material-symbols:delete-outline"
                              style={{ width: 20, height: 20 }}
                            />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        sx={{
                          color: "#5F5F5F",
                          fontFamily: fontFamily,
                          fontWeight: 500,
                          py: 4,
                        }}
                      >
                        No customer assigned
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Stack>
          </TableContainer>
        </CardContent>
      </Card>

      {deleteDialog.visible && (
        <DeleteCustomerPopup
          open
          onClose={() => deleteDialog.hide()}
          customerId={selectedCustomerId!}
          userDetailsId={salesPersonId?.id}
        />
      )}
    </Stack>
  );
};

export { SalesPersonDetails };
